import { useQuery } from '@tanstack/react-query';
import { MotifAvatar, MotifButton } from '@ey-xd/motif-wc-react';
import { authModule } from '@/auth/auth-module';
import { useHeaderStore } from '@/header/header-store';
import { useClientStore } from '@/client/client-store';
import { navigateBrowserToLegacyTarget } from '@/router/legacy-route-adapter';
import { getTracker } from '@/telemetry/tracker';
import { useUserAvatar } from '@/header/user-avatar';
import { useUserDisplay } from '@/header/use-user-display';
import { rumRolesForProfileQueryOptions, USER_ROLES_STALE_TIME_MS } from '@/header/user-roles';

/**
 * "My Account" panel body — mirrors the legacy `app-ey-header-user` flyout
 * (avatar · name · email · roles · log out · notification preferences).
 * Rendered inside the dark `.header-utility-panel--user` slide-in.
 */
export function UserPanel() {
  const closePanel = useHeaderStore((s) => s.closePanel);
  const currentClientId = useClientStore((s) => s.current?.id);
  const { name, email, avatarSeed } = useUserDisplay();
  const { data: avatarSrc = '' } = useUserAvatar(email);

  // Roles for the active client. Mirrors legacy
  // `HeaderUserService.getRumRolesForProfile()` →
  // `GetRumRolesForProfilePage?clientGuid=` → `userProfile[0].roleNames`.
  const roleQueryOptions = currentClientId
    ? rumRolesForProfileQueryOptions(currentClientId)
    : {
        queryKey: ['rumRolesForProfile', ''] as const,
        queryFn: async () => [] as string[],
        staleTime: USER_ROLES_STALE_TIME_MS,
      };
  const { data: roleNames = [] } = useQuery({
    ...roleQueryOptions,
    enabled: !!currentClientId,
  });

  return (
    <div className="header-utility-panel__user">
      <div className="header-utility-panel__user-identity">
        {/* CLD `ey-avatar [size]="'medium'"` (36px) — match sizing rather than
         * using Motif's larger `lg` default. */}
        <MotifAvatar
          size="md"
          variant={avatarSrc ? 'image' : 'initials'}
          src={avatarSrc || undefined}
          userName={name}
          initialsTextOverride={avatarSrc ? undefined : avatarSeed || undefined}
          showDefaultTooltip={false}
        />
        <div className="header-utility-panel__user-meta">
          <strong>{name || 'Not signed in'}</strong>
          {email && email !== name ? <span>{email}</span> : null}
        </div>
      </div>

      {roleNames.length > 0 ? (
        <div className="header-utility-panel__user-roles">
          <p className="header-utility-panel__user-roles-label">Roles:</p>
          <ul>
            {roleNames.map((role) => (
              <li key={role}>{role}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <MotifButton
        variant="secondary"
        type="button"
        fullWidth
        className="header-utility-panel__user-logout"
        onClick={() => {
          getTracker().event('Header-user-logout');
          authModule.logout();
        }}
      >
        Log Out
      </MotifButton>

      <a
        className="header-utility-panel__user-link"
        href="/app/notification-preferences"
        onClick={(e) => {
          e.preventDefault();

          closePanel();
          // Legacy `notificationPreferencesClicked` →
          // `routeToMFE('NOTIFICATION-PREFERENCES')` (the notification-preferences
          // MFE), NOT the shell Settings page.
          navigateBrowserToLegacyTarget('NOTIFICATION-PREFERENCES');
        }}
      >
        Notification Preferences
      </a>
    </div>
  );
}
